import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type ViewMode = "desktop" | "mobile";

interface ViewModeContextType {
  viewMode: ViewMode;
  setOverride: (mode: ViewMode | null) => void;
  override: ViewMode | null;
}

const ViewModeContext = createContext<ViewModeContextType>({
  viewMode: "mobile",
  setOverride: () => {},
  override: null,
});

const STORAGE_KEY = "viewmode-override";
const BREAKPOINT = 768;

export function ViewModeProvider({ children }: { children: ReactNode }) {
  // R-1: the override is a DEV-only tool (DevModeToggle renders null in prod),
  // but the localStorage key persists across builds — a stale "mobile" override
  // left prod users on ≥1024px with MobileLayout AND a lg:hidden BottomNav = no
  // navigation at all. In prod: ignore and clean up the key.
  const [override, setOverrideState] = useState<ViewMode | null>(() => {
    if (typeof window === "undefined") return null;
    if (!import.meta.env.DEV) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return (localStorage.getItem(STORAGE_KEY) as ViewMode | null);
  });

  const [screenMode, setScreenMode] = useState<ViewMode>(
    typeof window !== "undefined" && window.innerWidth >= BREAKPOINT ? "desktop" : "mobile"
  );

  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${BREAKPOINT}px)`);
    const onChange = () => setScreenMode(mql.matches ? "desktop" : "mobile");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const setOverride = (mode: ViewMode | null) => {
    setOverrideState(mode);
    if (mode) {
      localStorage.setItem(STORAGE_KEY, mode);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const viewMode = override ?? screenMode;

  return (
    <ViewModeContext.Provider value={{ viewMode, setOverride, override }}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode() {
  return useContext(ViewModeContext);
}
